import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Pencil, Plus, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CATEGORY_LABEL } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EMPTY = { name: "", category: "landmark", city: "San Marcos", address: "", state: "TX", zip: "", lat: "", lng: "", aliases: "", pricing_zone: "", notes: "" };
const TEMPLATE = "name,category,city,address\nExample Apartments,apartment,San Marcos,\"123 Example St, San Marcos, TX 78666\"\n";
const num = (v) => (v === "" || v == null ? null : parseFloat(v));

function LocationDialog({ open, onClose, initial }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial, lat: initial.lat ?? "", lng: initial.lng ?? "", aliases: (initial.aliases || []).join(", ") } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { name: form.name, category: form.category, city: form.city, address: form.address, state: form.state, zip: form.zip,
      pricing_zone: form.pricing_zone, notes: form.notes, lat: num(form.lat), lng: num(form.lng), source_category: form.source_category || "",
      aliases: form.aliases.split(",").map((a) => a.trim()).filter(Boolean) };
    try {
      const { data } = initial?.id ? await api.put(`/admin/locations/${initial.id}`, body) : await api.post("/admin/locations", body);
      if (data.needs_pin) toast.warning("Saved, but we couldn't find that address. It stays hidden until you add a pin.");
      else toast.success("Location saved");
      qc.invalidateQueries({ queryKey: ["adminLocations"] });
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="location-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit location" : "Add location"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1"><Label>Name</Label><Input required value={form.name} onChange={set("name")} data-testid="location-name-input" /></div>
          <div className="space-y-1">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="location-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CATEGORY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>City</Label><Input required value={form.city} onChange={set("city")} data-testid="location-city-input" /></div>
          <div className="col-span-2 space-y-1"><Label>Address</Label><Input value={form.address} onChange={set("address")} data-testid="location-address-input" /></div>
          <div className="space-y-1"><Label>State</Label><Input value={form.state} onChange={set("state")} data-testid="location-state-input" /></div>
          <div className="space-y-1"><Label>ZIP</Label><Input value={form.zip} onChange={set("zip")} data-testid="location-zip-input" /></div>
          <div className="col-span-2 space-y-1"><Label>Aliases (comma separated)</Label><Input value={form.aliases} onChange={set("aliases")} data-testid="location-aliases-input" /></div>
          <div className="space-y-1"><Label>Pricing zone</Label><Input value={form.pricing_zone} onChange={set("pricing_zone")} data-testid="location-zone-input" /></div>
          <div className="space-y-1"><Label>Notes</Label><Input value={form.notes} onChange={set("notes")} data-testid="location-notes-input" /></div>
          <div className="space-y-1"><Label>Latitude (optional)</Label><Input type="number" step="any" placeholder="From address" value={form.lat} onChange={set("lat")} data-testid="location-lat-input" /></div>
          <div className="space-y-1"><Label>Longitude (optional)</Label><Input type="number" step="any" placeholder="From address" value={form.lng} onChange={set("lng")} data-testid="location-lng-input" /></div>
          <p className="col-span-2 text-xs text-muted-foreground">Leave coordinates empty and we'll place it from the address. Clear them to re-locate from an edited address.</p>
          <DialogFooter className="col-span-2 mt-2">
            <Button type="submit" disabled={busy} data-testid="location-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CsvImport() {
  const qc = useQueryClient();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/locations/import", fd);
      toast.success(`Imported: ${data.inserted} new, ${data.updated} updated`, {
        description: [data.locating ? `Finding ${data.locating} addresses on the map — refresh in a minute or two.` : "",
          data.errors.length ? `${data.errors.length} rows skipped — ${data.errors.slice(0, 3).join("; ")}` : ""].filter(Boolean).join(" ") || undefined,
      });
      qc.invalidateQueries({ queryKey: ["adminLocations"] });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const template = () => {
    const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
    Object.assign(document.createElement("a"), { href: url, download: "scoopz-directory-template.csv" }).click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <Button variant="ghost" onClick={template} data-testid="csv-template-button"><Download className="mr-1 h-4 w-4" /> Template</Button>
      <Button variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid="csv-import-button">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} Import CSV
      </Button>
      <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="csv-file-input" />
    </>
  );
}

export default function AdminDirectory() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [city, setCity] = useState("all");
  const [cat, setCat] = useState("all");
  const [pin, setPin] = useState("all");
  const [params] = useSearchParams();
  const [editing, setEditing] = useState(params.get("add") ? {} : null);
  const [merge, setMerge] = useState(null);
  const doMerge = async () => {
    try {
      await api.post("/admin/locations/merge", merge);
      toast.success("Duplicates merged — names kept as aliases");
      setMerge(null);
      qc.invalidateQueries({ queryKey: ["adminLocations"] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const { data: meta } = useQuery({ queryKey: ["locMeta"], queryFn: () => api.get("/locations/meta").then((r) => r.data) });
  const { data } = useQuery({
    queryKey: ["adminLocations", q, city, cat, pin],
    queryFn: () => api.get("/admin/locations", { params: { q: q || undefined, city: city === "all" ? undefined : city,
      category: cat === "all" ? undefined : cat, needs_pin: pin === "all" ? undefined : pin === "needs" } }).then((r) => r.data),
  });

  const remove = async (l) => {
    if (l.active !== false && !window.confirm(`Deactivate ${l.name}? Riders won't see it.`)) return;
    try {
      await api.post(`/admin/locations/${l.id}/active`, { active: l.active === false });
      toast.success(l.active === false ? "Location reactivated" : "Location deactivated");
      qc.invalidateQueries({ queryKey: ["adminLocations"] });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="fade-up" data-testid="admin-directory">
      <PageHead title="Directory" sub={`${data?.length ?? "…"} place${data?.length === 1 ? "" : "s"} shown. CSV columns: name, category, city, address — coordinates are optional and found from the address.`}>
        <CsvImport />
        <Button variant="outline" onClick={() => setMerge(merge ? null : { source_id: "", target_id: "" })} data-testid="merge-locations-button">Merge duplicates</Button>
        <Button onClick={() => setEditing({})} data-testid="add-location-button"><Plus className="mr-1 h-4 w-4" /> Add place</Button>
      </PageHead>
      {merge && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border bg-card p-3 text-sm" data-testid="merge-panel">
          Merge
          {["source_id", "target_id"].map((k, i) => (
            <select key={k} value={merge[k]} onChange={(e) => setMerge({ ...merge, [k]: e.target.value })} className="h-9 max-w-xs rounded-md border bg-background px-2" data-testid={`merge-${k}`}>
              <option value="">{i ? "into (keep)…" : "duplicate…"}</option>
              {data?.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.city}</option>)}
            </select>
          ))}
          <Button size="sm" disabled={!merge.source_id || !merge.target_id} onClick={doMerge} data-testid="merge-confirm">Merge</Button>
        </div>
      )}
      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Search places" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-60" data-testid="directory-search-input" />
        <Select value={city} onValueChange={setCity}>
          <SelectTrigger className="h-9 w-44" data-testid="directory-city-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {meta?.cities?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="h-9 w-52" data-testid="directory-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={pin} onValueChange={setPin}>
          <SelectTrigger className="h-9 w-40" data-testid="directory-pin-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All pins</SelectItem>
            <SelectItem value="needs">Needs pin</SelectItem>
            <SelectItem value="ok">Pinned</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>City</TableHead><TableHead>Coordinates</TableHead><TableHead /></TableRow>
          </TableHeader>
          <TableBody>
            {data?.map((l) => (
              <TableRow key={l.id} data-testid="directory-row">
                <TableCell><p className={`font-semibold ${l.active === false ? "text-muted-foreground line-through" : ""}`}>{l.name}</p><p className="text-xs text-muted-foreground">{l.address}{l.aliases?.length ? ` · aka ${l.aliases.join(", ")}` : ""}</p></TableCell>
                <TableCell className="text-sm">{CATEGORY_LABEL[l.category] || l.category}{l.source_category && <p className="text-xs text-muted-foreground">{l.source_category}</p>}</TableCell>
                <TableCell className="text-sm">{l.city}</TableCell>
                <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                  {l.needs_pin || l.lat == null
                    ? <span className="rounded-full border border-amber-400/50 bg-amber-400/10 px-2 py-0.5 font-sans font-semibold text-amber-200" data-testid={`needs-pin-${l.id}`}>Needs pin</span>
                    : `${l.lat.toFixed(4)}, ${l.lng.toFixed(4)}`}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button size="icon" variant="ghost" onClick={() => setEditing(l)} data-testid={`edit-location-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                  {!l.needs_pin && <Button size="sm" variant="ghost" onClick={() => remove(l)} data-testid={`toggle-location-${l.id}`}>{l.active === false ? "Reactivate" : "Deactivate"}</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data?.length === 0 && <p className="p-5 text-sm text-muted-foreground">No places match.</p>}
      </div>
      {editing && <LocationDialog open initial={editing.id ? editing : null} onClose={() => setEditing(null)} />}
    </div>
  );
}
